<template>
  <div class="item-form" :class="{ 'create-mode': isCreateMode }">
    <div class="form-grid">
      <BaseFormField
        class="form-group--wide"
        label="Продукт из реестра"
        required
        :error="errors.selectedProductId"
        error-message="Выберите продукт из реестра"
      >
        <template #action>
          <BaseActionLink :to="{ name: 'ProductsRegistry' }">
            Открыть реестр продуктов
          </BaseActionLink>
        </template>

        <base-select
          v-model="selectedProductId"
          :options="productOptions"
          placeholder="Выбрать продукт"
          :clearable="false"
          :searchable="true"
          :with-button="true"
          button-label="Создать новый продукт"
          no-results-text="Продукты не найдены"
          :invalid="errors.selectedProductId"
          @change="applyProduct"
          @button-click="toggleInlineProductForm"
        />

        <div
          v-if="isProductsEmpty && !showInlineProductForm"
          class="empty-registry-warning"
        >
          <span class="warning-icon">⚠️</span>
          Реестр продуктов пуст.
          <button class="link-btn" @click="toggleInlineProductForm">
            Создать первый продукт
          </button>
        </div>
      </BaseFormField>

      <div
        v-if="showInlineProductForm"
        class="form-group form-group--wide"
      >
        <div class="inline-product-form">
          <div class="inline-form-header">
            <span class="inline-form-title">Новый продукт</span>
            <button
              class="inline-form-close"
              @click="showInlineProductForm = false"
            >
              ✕
            </button>
          </div>

          <div class="inline-form-grid">
            <div class="inline-form-group inline-form-group--wide">
              <base-label :required="true">Название</base-label>
              <base-input
                v-model="productForm.name"
                placeholder="Название продукта"
                :invalid="productErrors.name"
                @input="clearProductError('name')"
              />
              <span v-if="productErrors.name" class="error-message">
                Это поле обязательно
              </span>
            </div>

            <div class="inline-form-group inline-form-group--wide">
              <base-label :required="true">Описание</base-label>
              <base-input
                v-model="productForm.description"
                placeholder="Описание продукта"
                :invalid="productErrors.description"
                @input="clearProductError('description')"
              />
              <span
                v-if="productErrors.description"
                class="error-message"
              >
                Это поле обязательно
              </span>
            </div>

            <div class="inline-form-group">
              <base-label :required="true">Код / артикул</base-label>
              <base-input
                v-model="productForm.code"
                placeholder="Код или артикул"
                :invalid="productErrors.code"
                @input="clearProductError('code')"
              />
              <span v-if="productErrors.code" class="error-message">
                Это поле обязательно
              </span>
            </div>

            <div class="inline-form-group">
              <base-label :required="true">
                Цена по умолчанию
              </base-label>
              <base-input
                v-model="productForm.price"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                :invalid="productErrors.price"
                @input="clearProductError('price')"
              />
              <span v-if="productErrors.price" class="error-message">
                Это поле обязательно
              </span>
            </div>

            <div class="inline-form-group">
              <base-label :required="true">
                Единица измерения
              </base-label>
              <base-input
                v-model="productForm.unit"
                placeholder="шт, кг, литров..."
                :invalid="productErrors.unit"
                @input="clearProductError('unit')"
              />
              <span v-if="productErrors.unit" class="error-message">
                Это поле обязательно
              </span>
            </div>

            <VatFields
              v-model:vatKind="productForm.vat_kind"
              v-model:vatRate="productForm.vat_rate"
              v-model:vatCalculationType="
                productForm.vat_calculation_type
              "
              :vat-kind-options="vatKindOptions"
              :vat-calculation-type-options="
                vatCalculationTypeOptions
              "
              :errors="productErrors"
              field-class="inline-form-group"
              @clear-error="clearProductError"
            />
          </div>

          <div class="inline-form-actions">
            <base-primary-button
              icon="addSquare"
              @click="saveInlineProduct"
              :loading="isProductsLoading"
              class="btn-sm"
            >
              Создать и выбрать
            </base-primary-button>
            <base-button
              @click="showInlineProductForm = false"
              class="btn-sm"
            >
              Отмена
            </base-button>
          </div>
        </div>
      </div>

      <div class="form-group form-group--wide">
        <base-label :required="true">Наименование</base-label>
        <base-input
          v-model="form.name"
          placeholder="Название услуги"
          :invalid="errors.name"
          @input="clearError('name')"
        />
        <span v-if="errors.name" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="form-group form-group--wide">
        <base-label>Описание</base-label>
        <base-input
          v-model="form.description"
          placeholder="Описание услуги"
        />
      </div>

      <div class="form-group">
        <base-label :required="true">Цена за единицу</base-label>
        <base-input
          v-model="form.price"
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          :invalid="errors.price"
          @input="clearError('price')"
        />
        <span v-if="errors.price" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="form-group">
        <base-label :required="true">Количество</base-label>
        <base-input
          v-model="form.quantity"
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          :invalid="errors.quantity"
          @input="clearError('quantity')"
        />
        <span v-if="errors.quantity" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="form-group">
        <base-label :required="true">Единица измерения</base-label>
        <base-input
          v-model="form.unit"
          placeholder="шт, кг, литров..."
          :invalid="errors.unit"
          @input="clearError('unit')"
        />
        <span v-if="errors.unit" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <VatFields
        v-model:vatKind="form.vat_kind"
        v-model:vatRate="form.vat_rate"
        v-model:vatCalculationType="form.vat_calculation_type"
        :vat-kind-options="vatKindOptions"
        :vat-calculation-type-options="vatCalculationTypeOptions"
        :errors="errors"
        field-class="form-group"
        :show-no-vat-hint="true"
        @clear-error="clearError"
      />
    </div>

    <div class="form-actions">
      <base-primary-button @click="save" :loading="isLoading">
        {{ isCreateMode ? "Добавить" : "Сохранить" }}
      </base-primary-button>
      <base-button @click="$emit('cancel')">Отмена</base-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import VatFields from "@/components/Shared/VatFields.vue";
import { useOrderInvoiceItemStore } from "@/stores/orderInvoiceItemStore";
import { useOrderInvoiceProductStore } from "@/stores/orderInvoiceProductStore";
import { useNotificationStore } from "@/stores/notificationStore";
import {
  nextVatRateForKind,
  payloadVatRate,
  vatRateIsInvalid,
} from "@/utils/vatUtils";

export default {
  name: "OrderInvoiceItemForm",
  components: { VatFields },
  props: {
    orderId: { type: [String, Number], required: true },
    invoiceId: { type: [String, Number], required: true },
    companyId: { type: [String, Number], required: true },
    initialData: { type: Object, default: null },
    isCreateMode: { type: Boolean, default: false },
  },
  emits: ["save", "cancel"],
  data() {
    return {
      selectedProductId: null,
      showInlineProductForm: false,
      productForm: {
        name: "",
        description: "",
        code: "",
        vat_kind: null,
        vat_rate: "",
        vat_calculation_type: "included",
        unit: "",
        price: "",
      },
      productErrors: {
        name: false,
        description: false,
        code: false,
        unit: false,
        price: false,
        vat_kind: false,
        vat_rate: false,
        vat_calculation_type: false,
      },
      form: {
        name: "",
        description: "",
        unit: "",
        quantity: 1,
        price: "",
        vat_kind: null,
        vat_rate: "",
        vat_calculation_type: "included",
      },
      errors: {
        selectedProductId: false,
        name: false,
        unit: false,
        quantity: false,
        price: false,
        vat_kind: false,
        vat_rate: false,
        vat_calculation_type: false,
      },
    };
  },
  computed: {
    ...mapState(useOrderInvoiceItemStore, ["isLoading"]),
    ...mapState(useOrderInvoiceProductStore, {
      activeProducts: "activeProducts",
      isProductsLoading: "isLoading",
      vatKindOptions: "vatKindOptions",
      vatCalculationTypeOptions: "vatCalculationTypeOptions",
    }),

    productOptions() {
      return this.activeProducts.map((p) => ({
        value: p.id,
        label: `${p.code}, ${p.name}`,
      }));
    },

    isProductsEmpty() {
      return this.activeProducts.length === 0;
    },

    notificationStore() {
      return useNotificationStore();
    },
  },
  methods: {
    ...mapActions(useOrderInvoiceItemStore, [
      "createItem",
      "updateItem",
    ]),
    ...mapActions(useOrderInvoiceProductStore, [
      "getProductsRequest",
      "createProduct",
    ]),

    initFormFromInitialData() {
      if (!this.initialData) return;

      this.selectedProductId =
        this.initialData.order_invoice_product_id || null;

      this.form = {
        name: this.initialData.name || "",
        description: this.initialData.description || "",
        unit: this.initialData.unit || "",
        quantity: this.initialData.quantity || 1,
        price: this.initialData.price || "",
        vat_kind: this.initialData.vat_kind || null,
        vat_rate: this.initialData.vat_rate ?? "",
        vat_calculation_type:
          this.initialData.vat_calculation_type || "included",
      };
    },

    toggleInlineProductForm() {
      this.showInlineProductForm = !this.showInlineProductForm;
      if (this.showInlineProductForm) {
        this.productForm = {
          name: "",
          description: "",
          code: "",
          vat_kind: null,
          vat_rate: "",
          vat_calculation_type: "included",
          unit: "",
          price: "",
        };
        this.productErrors = {
          name: false,
          description: false,
          code: false,
          unit: false,
          price: false,
          vat_kind: false,
          vat_rate: false,
          vat_calculation_type: false,
        };
      }
    },

    validateProductForm() {
      let isValid = true;
      this.productErrors = {
        name: false,
        description: false,
        code: false,
        unit: false,
        price: false,
        vat_kind: false,
        vat_rate: false,
        vat_calculation_type: false,
      };

      if (!String(this.productForm.name).trim()) {
        this.productErrors.name = true;
        isValid = false;
      }
      if (!String(this.productForm.description).trim()) {
        this.productErrors.description = true;
        isValid = false;
      }
      if (!String(this.productForm.code).trim()) {
        this.productErrors.code = true;
        isValid = false;
      }
      if (!String(this.productForm.unit).trim()) {
        this.productErrors.unit = true;
        isValid = false;
      }
      if (
        this.productForm.price === "" ||
        this.productForm.price === null ||
        parseFloat(this.productForm.price) < 0
      ) {
        this.productErrors.price = true;
        isValid = false;
      }
      if (!this.productForm.vat_kind) {
        this.productErrors.vat_kind = true;
        isValid = false;
      }
      if (this.productForm.vat_kind === "with_vat") {
        if (
          vatRateIsInvalid(
            this.productForm.vat_kind,
            this.productForm.vat_rate,
            { allowZeroWithVat: true }
          ) ||
          parseFloat(this.productForm.vat_rate) > 100
        ) {
          this.productErrors.vat_rate = true;
          isValid = false;
        }
      }
      if (!this.productForm.vat_calculation_type) {
        this.productErrors.vat_calculation_type = true;
        isValid = false;
      }

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
      }

      return isValid;
    },

    clearProductError(field) {
      if (this.productErrors[field]) {
        this.productErrors[field] = false;
      }
    },

    async saveInlineProduct() {
      if (!this.validateProductForm()) return;

      const payload = {
        name: this.productForm.name,
        description: this.productForm.description,
        code: this.productForm.code,
        vat_kind: this.productForm.vat_kind,
        vat_rate: payloadVatRate(
          this.productForm.vat_kind,
          this.productForm.vat_rate,
          { zeroKindAsZero: false }
        ),
        vat_calculation_type: this.productForm.vat_calculation_type,
        unit: this.productForm.unit,
        price: parseFloat(this.productForm.price),
      };

      try {
        const created = await this.createProduct(payload);

        this.selectedProductId = created.id;
        this.applyProduct({ value: created.id });

        this.showInlineProductForm = false;
        this.notificationStore.showSuccess("Продукт создан и выбран");
        this.clearError("selectedProductId");
      } catch (error) {
        console.error("Ошибка создания продукта:", error);
      }
    },

    applyProduct(option) {
      this.clearError("selectedProductId");
      if (!option) return;

      const product = this.activeProducts.find(
        (p) => p.id === option.value
      );
      if (!product) return;

      this.form.name = product.name;
      this.form.description = product.description || "";
      this.form.unit = product.unit || "";
      this.form.price = product.price || "";
      this.form.vat_kind = product.vat_kind ?? null;
      this.form.vat_calculation_type =
        product.vat_calculation_type || "included";

      const nextRate = nextVatRateForKind(
        product.vat_kind,
        product.vat_rate,
        { useDefaultVatRate: false }
      );
      if (nextRate !== null) {
        this.form.vat_rate = nextRate;
      } else {
        this.form.vat_rate = product.vat_rate ?? "";
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = false;
      }
    },

    validateForm() {
      let isValid = true;
      this.errors = {
        selectedProductId: false,
        name: false,
        unit: false,
        quantity: false,
        price: false,
        vat_kind: false,
        vat_rate: false,
        vat_calculation_type: false,
      };

      if (!this.selectedProductId) {
        this.errors.selectedProductId = true;
        isValid = false;
      }
      if (!this.form.name || !String(this.form.name).trim()) {
        this.errors.name = true;
        isValid = false;
      }
      if (!this.form.unit || !String(this.form.unit).trim()) {
        this.errors.unit = true;
        isValid = false;
      }
      if (
        !this.form.quantity ||
        parseFloat(this.form.quantity) <= 0
      ) {
        this.errors.quantity = true;
        isValid = false;
      }
      if (
        this.form.price === "" ||
        this.form.price === null ||
        parseFloat(this.form.price) < 0
      ) {
        this.errors.price = true;
        isValid = false;
      }
      if (!this.form.vat_kind) {
        this.errors.vat_kind = true;
        isValid = false;
      }
      if (this.form.vat_kind === "with_vat") {
        if (
          vatRateIsInvalid(this.form.vat_kind, this.form.vat_rate, {
            allowZeroWithVat: true,
          }) ||
          parseFloat(this.form.vat_rate) > 100
        ) {
          this.errors.vat_rate = true;
          isValid = false;
        }
      }
      if (!this.form.vat_calculation_type) {
        this.errors.vat_calculation_type = true;
        isValid = false;
      }

      return isValid;
    },

    async save() {
      const isValid = this.validateForm();

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
        return;
      }

      const payload = {
        name: this.form.name,
        description: this.form.description,
        unit: this.form.unit,
        quantity: parseFloat(this.form.quantity),
        price: parseFloat(this.form.price),
        vat_kind: this.form.vat_kind,
        vat_rate: payloadVatRate(this.form.vat_kind, this.form.vat_rate, {
          zeroKindAsZero: false,
        }),
        vat_calculation_type: this.form.vat_calculation_type,
        order_invoice_id: Number(this.invoiceId),
        order_invoice_product_id: this.selectedProductId,
      };

      try {
        if (this.isCreateMode) {
          await this.createItem(
            this.orderId,
            this.invoiceId,
            payload
          );
        } else {
          await this.updateItem(
            this.orderId,
            this.invoiceId,
            this.initialData.id,
            payload
          );
        }
        this.$emit("save");
      } catch (error) {
        console.error("Ошибка сохранения позиции:", error);
      }
    },
  },
  async created() {
    await this.getProductsRequest();

    this.initFormFromInitialData();
  },
};
</script>

<style scoped>
.item-form {
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.create-mode {
  margin-bottom: var(--base-in-indent);
  border: 1px solid #e8e8e8;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-group--wide {
  grid-column: span 2;
}

.error-message {
  color: var(--color-danger);
  font-size: 12px;
}

.hint-message {
  font-size: 12px;
  color: #6b7280;
}

.empty-registry-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 13px;
  color: #ca8a04;
  background: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 8px 12px;
}

.warning-icon {
  flex-shrink: 0;
}

.link-btn {
  background: none;
  border: none;
  padding: 0;
  color: #4f46e5;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
}

.link-btn:hover {
  color: #3730a3;
}

.inline-product-form {
  border-radius: 8px;
  padding: 12px;
  border: 2px dashed #b6b5b5;
  background: #e6f5ed;
}

.inline-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.inline-form-title {
  font-weight: 600;
  color: #374151;
}

.inline-form-close {
  background: none;
  border: none;
  font-size: 14px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.inline-form-close:hover {
  color: #374151;
}

.inline-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.inline-form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inline-form-group--wide {
  grid-column: span 2;
}

.inline-form-actions {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 14px;
  font-size: 13px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: var(--base-in-indent);
}

@media (max-width: 600px) {
  .form-grid,
  .inline-form-grid {
    grid-template-columns: 1fr;
  }

  .form-group--wide,
  .inline-form-group--wide {
    grid-column: span 1;
  }
}
</style>
